/**
 * Converts Vietnamese text to URL-friendly slug while preserving user input experience
 * - Removes Vietnamese accents/diacritics
 * - Converts to lowercase
 * - Replaces spaces with hyphens in real-time
 * - Removes consecutive hyphens
 * - Trims hyphens from start/end
 */
export function convertToSlug(text: string): string {
  if (!text) return ''

  // Vietnamese character mapping
  const vietnameseMap: Record<string, string> = {
    // a variations
    á: 'a',
    à: 'a',
    ả: 'a',
    ã: 'a',
    ạ: 'a',
    ă: 'a',
    ắ: 'a',
    ằ: 'a',
    ẳ: 'a',
    ẵ: 'a',
    ặ: 'a',
    â: 'a',
    ấ: 'a',
    ầ: 'a',
    ẩ: 'a',
    ẫ: 'a',
    ậ: 'a',
    // e variations
    é: 'e',
    è: 'e',
    ẻ: 'e',
    ẽ: 'e',
    ẹ: 'e',
    ê: 'e',
    ế: 'e',
    ề: 'e',
    ể: 'e',
    ễ: 'e',
    ệ: 'e',
    // i variations
    í: 'i',
    ì: 'i',
    ỉ: 'i',
    ĩ: 'i',
    ị: 'i',
    // o variations
    ó: 'o',
    ò: 'o',
    ỏ: 'o',
    õ: 'o',
    ọ: 'o',
    ô: 'o',
    ố: 'o',
    ồ: 'o',
    ổ: 'o',
    ỗ: 'o',
    ộ: 'o',
    ơ: 'o',
    ớ: 'o',
    ờ: 'o',
    ở: 'o',
    ỡ: 'o',
    ợ: 'o',
    // u variations
    ú: 'u',
    ù: 'u',
    ủ: 'u',
    ũ: 'u',
    ụ: 'u',
    ư: 'u',
    ứ: 'u',
    ừ: 'u',
    ử: 'u',
    ữ: 'u',
    ự: 'u',
    // y variations
    ý: 'y',
    ỳ: 'y',
    ỷ: 'y',
    ỹ: 'y',
    ỵ: 'y',
    // d variations
    đ: 'd',
    // Uppercase versions
    Á: 'a',
    À: 'a',
    Ả: 'a',
    Ã: 'a',
    Ạ: 'a',
    Ă: 'a',
    Ắ: 'a',
    Ằ: 'a',
    Ẳ: 'a',
    Ẵ: 'a',
    Ặ: 'a',
    Â: 'a',
    Ấ: 'a',
    Ầ: 'a',
    Ẩ: 'a',
    Ẫ: 'a',
    Ậ: 'a',
    É: 'e',
    È: 'e',
    Ẻ: 'e',
    Ẽ: 'e',
    Ẹ: 'e',
    Ê: 'e',
    Ế: 'e',
    Ề: 'e',
    Ể: 'e',
    Ễ: 'e',
    Ệ: 'e',
    Í: 'i',
    Ì: 'i',
    Ỉ: 'i',
    Ĩ: 'i',
    Ị: 'i',
    Ó: 'o',
    Ò: 'o',
    Ỏ: 'o',
    Õ: 'o',
    Ọ: 'o',
    Ô: 'o',
    Ố: 'o',
    Ồ: 'o',
    Ổ: 'o',
    Ỗ: 'o',
    Ộ: 'o',
    Ơ: 'o',
    Ớ: 'o',
    Ờ: 'o',
    Ở: 'o',
    Ỡ: 'o',
    Ợ: 'o',
    Ú: 'u',
    Ù: 'u',
    Ủ: 'u',
    Ũ: 'u',
    Ụ: 'u',
    Ư: 'u',
    Ứ: 'u',
    Ừ: 'u',
    Ử: 'u',
    Ữ: 'u',
    Ự: 'u',
    Ý: 'y',
    Ỳ: 'y',
    Ỷ: 'y',
    Ỹ: 'y',
    Ỵ: 'y',
    Đ: 'd',
  }

  return (
    text
      // Convert to lowercase first
      .toLowerCase()
      // Replace Vietnamese characters
      .replace(/[^\w\s-]/g, char => vietnameseMap[char] || char)
      // Remove remaining special characters except letters, numbers, spaces, and hyphens
      .replace(/[^\w\s-]/g, '')
      // Replace multiple spaces/whitespace with single space
      .replace(/\s+/g, ' ')
      // Trim spaces from start and end
      .trim()
      // Replace spaces with hyphens
      .replace(/\s/g, '-')
      // Remove multiple consecutive hyphens
      .replace(/-+/g, '-')
      // Remove hyphens from start and end
      .replace(/^-+|-+$/g, '')
  )
}

/**
 * Converts text to slug in real-time for input fields
 * - Allows users to type spaces naturally
 * - Converts spaces to hyphens immediately
 * - Handles Vietnamese characters
 * - More user-friendly for real-time input
 */
export function convertToSlugRealtime(text: string): string {
  if (!text) return ''

  // Vietnamese character mapping (same as above)
  const vietnameseMap: Record<string, string> = {
    // a variations
    á: 'a',
    à: 'a',
    ả: 'a',
    ã: 'a',
    ạ: 'a',
    ă: 'a',
    ắ: 'a',
    ằ: 'a',
    ẳ: 'a',
    ẵ: 'a',
    ặ: 'a',
    â: 'a',
    ấ: 'a',
    ầ: 'a',
    ẩ: 'a',
    ẫ: 'a',
    ậ: 'a',
    // e variations
    é: 'e',
    è: 'e',
    ẻ: 'e',
    ẽ: 'e',
    ẹ: 'e',
    ê: 'e',
    ế: 'e',
    ề: 'e',
    ể: 'e',
    ễ: 'e',
    ệ: 'e',
    // i variations
    í: 'i',
    ì: 'i',
    ỉ: 'i',
    ĩ: 'i',
    ị: 'i',
    // o variations
    ó: 'o',
    ò: 'o',
    ỏ: 'o',
    õ: 'o',
    ọ: 'o',
    ô: 'o',
    ố: 'o',
    ồ: 'o',
    ổ: 'o',
    ỗ: 'o',
    ộ: 'o',
    ơ: 'o',
    ớ: 'o',
    ờ: 'o',
    ở: 'o',
    ỡ: 'o',
    ợ: 'o',
    // u variations
    ú: 'u',
    ù: 'u',
    ủ: 'u',
    ũ: 'u',
    ụ: 'u',
    ư: 'u',
    ứ: 'u',
    ừ: 'u',
    ử: 'u',
    ữ: 'u',
    ự: 'u',
    // y variations
    ý: 'y',
    ỳ: 'y',
    ỷ: 'y',
    ỹ: 'y',
    ỵ: 'y',
    // d variations
    đ: 'd',
    // Uppercase versions
    Á: 'a',
    À: 'a',
    Ả: 'a',
    Ã: 'a',
    Ạ: 'a',
    Ă: 'a',
    Ắ: 'a',
    Ằ: 'a',
    Ẳ: 'a',
    Ẵ: 'a',
    Ặ: 'a',
    Â: 'a',
    Ấ: 'a',
    Ầ: 'a',
    Ẩ: 'a',
    Ẫ: 'a',
    Ậ: 'a',
    É: 'e',
    È: 'e',
    Ẻ: 'e',
    Ẽ: 'e',
    Ẹ: 'e',
    Ê: 'e',
    Ế: 'e',
    Ề: 'e',
    Ể: 'e',
    Ễ: 'e',
    Ệ: 'e',
    Í: 'i',
    Ì: 'i',
    Ỉ: 'i',
    Ĩ: 'i',
    Ị: 'i',
    Ó: 'o',
    Ò: 'o',
    Ỏ: 'o',
    Õ: 'o',
    Ọ: 'o',
    Ô: 'o',
    Ố: 'o',
    Ồ: 'o',
    Ổ: 'o',
    Ỗ: 'o',
    Ộ: 'o',
    Ơ: 'o',
    Ớ: 'o',
    Ờ: 'o',
    Ở: 'o',
    Ỡ: 'o',
    Ợ: 'o',
    Ú: 'u',
    Ù: 'u',
    Ủ: 'u',
    Ũ: 'u',
    Ụ: 'u',
    Ư: 'u',
    Ứ: 'u',
    Ừ: 'u',
    Ử: 'u',
    Ữ: 'u',
    Ự: 'u',
    Ý: 'y',
    Ỳ: 'y',
    Ỷ: 'y',
    Ỹ: 'y',
    Ỵ: 'y',
    Đ: 'd',
  }

  return (
    text
      // Replace Vietnamese characters first (before converting to lowercase)
      .split('')
      .map(char => vietnameseMap[char] || char)
      .join('')
      // Convert to lowercase
      .toLowerCase()
      // Replace spaces with hyphens immediately
      .replace(/\s+/g, '-')
      // Remove special characters except letters, numbers, and hyphens
      .replace(/[^a-z0-9-]/g, '')
      // Remove consecutive hyphens
      .replace(/-+/g, '-')
      // Remove hyphens from start and end
      .replace(/^-+|-+$/g, '')
  )
}

/**
 * Alternative version using modern browser APIs (Intl.Collator)
 * More efficient but requires modern browser support
 */
export function convertToSlugModern(text: string): string {
  if (!text) return ''

  return (
    text
      .toLowerCase()
      // Normalize unicode characters and remove diacritics
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      // Handle Vietnamese đ/Đ specifically
      .replace(/đ/g, 'd')
      .replace(/Đ/g, 'd')
      // Remove special characters except letters, numbers, spaces, hyphens
      .replace(/[^\w\s-]/g, '')
      // Replace multiple spaces with single space
      .replace(/\s+/g, ' ')
      .trim()
      // Replace spaces with hyphens
      .replace(/\s/g, '-')
      // Remove consecutive hyphens
      .replace(/-+/g, '-')
      // Trim hyphens
      .replace(/^-+|-+$/g, '')
  )
}

/**
 * Validates if a string is a valid slug format
 */
export function isValidSlug(slug: string): boolean {
  // Valid slug: lowercase letters, numbers, hyphens only
  // Cannot start/end with hyphen, no consecutive hyphens
  const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
  return slugRegex.test(slug)
}

/**
 * Ensures uniqueness by appending number if needed
 */
export function ensureUniqueSlug(baseSlug: string, existingSlugs: string[]): string {
  let slug = baseSlug
  let counter = 1

  while (existingSlugs.includes(slug)) {
    slug = `${baseSlug}-${counter}`
    counter++
  }

  return slug
}

// Export the real-time function as default for better UX
export default convertToSlugRealtime
